import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChantierHoverPreview from "@/components/ChantierHoverPreview";
import TeamMapDialog from "@/components/TeamMapDialog";
import WeatherWidget from "@/components/WeatherWidget";
import TechHoursCard from "@/components/TechHoursCard";
import PayrollCard from "@/components/PayrollCard";
import TresoreriePanel from "@/components/TresoreriePanel";
import {
  HardHat, Users, Clock, WarningCircle, Sparkle, ArrowRight,
  CalendarBlank, ChartLineUp, Lightning, MapTrifold, House, CheckCircle,
} from "@phosphor-icons/react";

/**
 * Full-width control tower for desktop.
 * Grid: 3 columns × auto rows.
 * - Top strip: 4 KPI cards (Chantiers actifs, Interventions du jour, Techs en course, Alertes)
 * - Left col: Interventions du jour (planning agenda-style)
 * - Center col: Prochains chantiers + Dépannages SAV
 * - Right col: Carte équipe temps réel + Alertes
 */
export default function DesktopDashboard() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [ivToday, setIvToday] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapOpen, setMapOpen] = useState(false);
  const [liveTechs, setLiveTechs] = useState(0);

  useEffect(() => {
    (async () => {
      const isoStart = new Date(); isoStart.setHours(0, 0, 0, 0);
      const isoEnd = new Date(); isoEnd.setHours(23, 59, 59, 999);
      const [i, c, u, cl, team] = await Promise.all([
        api.get("/interventions", { params: { start: isoStart.toISOString().slice(0, 10), end: isoEnd.toISOString().slice(0, 10) + "T23:59" } }),
        api.get("/chantiers"),
        api.get("/users"),
        api.get("/clients"),
        // Live team positions — used to count who's actually out there right
        // now instead of counting "started chrono" interventions.
        api.get("/users/team-locations").catch(() => ({ data: [] })),
      ]);
      setIvToday((i.data || []).sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)));
      setChantiers(c.data || []);
      setUsers((u.data || []).filter((x) => x.is_active !== false));
      setClients(Object.fromEntries((cl.data || []).map((x) => [x.id, x])));
      setLiveTechs((team.data || []).filter((t) => t.is_live).length);
      // Alerts: chantiers urgent + interventions dépannage
      const urg = (c.data || []).filter((x) => x.status === "urgent");
      const sav = (i.data || []).filter((x) => x.type === "depannage" && x.status !== "done");
      setAlerts([
        ...urg.map((x) => ({ kind: "chantier-urgent", label: x.name, id: x.id })),
        ...sav.map((x) => ({ kind: "iv-sav", label: x.title, id: `iv-${x.id}`, chantier_id: x.chantier_id })),
      ]);
      setLoading(false);
    })();
  }, []);

  const chantiersById = Object.fromEntries(chantiers.map((c) => [c.id, c]));
  const activeChantiers = chantiers.filter((c) => c.status !== "done");
  const upcomingChantiers = activeChantiers.slice(0, 5);

  const kpi = [
    { label: "Chantiers actifs", value: activeChantiers.length, icon: HardHat, color: "#005CFF", onClick: () => nav("/chantiers") },
    { label: "Interventions aujourd'hui", value: ivToday.length, icon: CalendarBlank, color: "#10B981", onClick: () => nav("/agenda") },
    // "Techs sur le terrain" = tech avec un ping GPS reçu dans les 15 min.
    // Clic → ouvre la carte équipe temps réel.
    { label: "Techs sur le terrain", value: liveTechs, icon: Users, color: "#F59E0B", onClick: () => setMapOpen(true) },
    { label: "Alertes", value: alerts.length, icon: WarningCircle, color: "#EF4444", onClick: () => nav("/alertes") },
  ];

  if (loading) {
    return (
      <div className="p-8 grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => <div key={i} className="h-28 rounded-2xl bg-white/5 animate-pulse" />)}
        <div className="col-span-4 h-96 rounded-2xl bg-white/5 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="desktop-dashboard">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpi.map((k, i) => (
          <button
            key={k.label}
            onClick={k.onClick}
            className="rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 p-5 text-left hover:border-white/25 transition-all group"
            data-testid={`kpi-${i}`}
          >
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: k.color + "22" }}>
                <k.icon size={22} weight="duotone" style={{ color: k.color }} />
              </div>
              <ArrowRight size={14} className="text-white/30 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all" weight="bold" />
            </div>
            <div className="mt-3 font-display text-3xl font-bold tabular-nums">{k.value}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">{k.label}</div>
          </button>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left: today's interventions */}
        <section className="col-span-12 xl:col-span-5 rounded-2xl bg-white/[0.03] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Aujourd&apos;hui</div>
              <div className="font-display text-lg font-semibold flex items-center gap-2">
                <CalendarBlank size={18} weight="duotone" className="text-[#7CB2FF]" />
                Interventions du jour
              </div>
            </div>
            <button onClick={() => nav("/agenda")} className="text-xs text-[#7CB2FF] hover:text-white flex items-center gap-1" data-testid="dash-agenda-link">
              Ouvrir l&apos;agenda <ArrowRight size={11} weight="bold" />
            </button>
          </div>
          {ivToday.length === 0 ? (
            <div className="text-center py-10 text-white/40 text-sm">
              <CheckCircle size={28} weight="duotone" className="mx-auto mb-2 text-emerald-500/60" />
              Aucune intervention aujourd&apos;hui
            </div>
          ) : (
            <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
              {ivToday.map((iv) => {
                const u = users.find((x) => x.id === iv.technician_id);
                const ch = iv.chantier_id ? chantiersById[iv.chantier_id] : null;
                const cl = ch && ch.client_id ? clients[ch.client_id] : null;
                return (
                  <ChantierHoverPreview key={iv.id} chantier={ch} client={cl} currentIv={iv} side="right">
                    <button
                      onClick={() => iv.chantier_id && nav(`/chantiers/${iv.chantier_id}`)}
                      className="w-full rounded-xl bg-white/[0.02] border border-white/8 hover:border-white/25 p-3 text-left transition-colors flex items-center gap-3"
                      data-testid={`dash-iv-${iv.id}`}
                    >
                      <div className="w-1 h-10 rounded-full shrink-0" style={{ background: iv.type === "depannage" ? "#F59E0B" : (u?.color || "#005CFF") }} />
                      <div className="text-xs font-mono tabular-nums text-white/70 shrink-0 w-12">
                        {new Date(iv.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                          {iv.type === "depannage" && <span className="text-[8px] bg-amber-500 text-black px-1 rounded font-bold">SAV</span>}
                          {iv.title}
                        </div>
                        <div className="text-[10px] text-white/50 truncate">
                          {u?.name} · {ch?.name || "—"} {iv.status === "done" && <span className="text-emerald-400">✓ Terminée</span>}
                        </div>
                      </div>
                      <ArrowRight size={12} weight="bold" className="text-white/30 shrink-0" />
                    </button>
                  </ChantierHoverPreview>
                );
              })}
            </div>
          )}
        </section>

        {/* Center: upcoming chantiers */}
        <section className="col-span-12 xl:col-span-4 rounded-2xl bg-white/[0.03] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Prochains</div>
              <div className="font-display text-lg font-semibold flex items-center gap-2">
                <HardHat size={18} weight="duotone" className="text-[#7CB2FF]" />
                Chantiers actifs
              </div>
            </div>
            <button onClick={() => nav("/chantiers")} className="text-xs text-[#7CB2FF] hover:text-white flex items-center gap-1" data-testid="dash-chantiers-link">
              Voir tout <ArrowRight size={11} weight="bold" />
            </button>
          </div>
          {upcomingChantiers.length === 0 ? (
            <div className="text-center py-10 text-white/40 text-sm">Aucun chantier en cours</div>
          ) : (
            <div className="space-y-2">
              {upcomingChantiers.map((c) => {
                const meta = c.status === "urgent"
                  ? { color: "#EF4444", label: "Urgent" }
                  : c.status === "in_progress" ? { color: "#005CFF", label: "En cours" }
                  : { color: "#9CA3AF", label: "En attente" };
                return (
                  <button
                    key={c.id}
                    onClick={() => nav(`/chantiers/${c.id}`)}
                    className="w-full rounded-xl bg-white/[0.02] border border-white/8 hover:border-white/25 p-3 text-left transition-colors"
                    data-testid={`dash-chantier-${c.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-white/50 truncate">{clients[c.client_id]?.name || "—"}</div>
                        <div className="text-sm font-medium truncate">{c.name}</div>
                      </div>
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0" style={{ background: meta.color + "22", color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="mt-2 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full transition-[width]" style={{ width: `${c.progress || 0}%`, background: meta.color }} />
                    </div>
                    <div className="text-[9px] text-white/40 mt-1 flex justify-between">
                      <span>{c.sous_chantiers?.length || 0} sous-chantiers</span>
                      <span>{c.progress || 0}%</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Right col: alerts + weather + team + AI */}
        <section className="col-span-12 xl:col-span-3 space-y-4">
          <WeatherWidget />

          {isAdmin && <TresoreriePanel />}

          <div className="rounded-2xl bg-gradient-to-br from-red-500/10 to-red-500/[0.02] border border-red-500/25 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-semibold flex items-center gap-1.5">
                <WarningCircle size={12} weight="fill" />
                Alertes · {alerts.length}
              </div>
            </div>
            {alerts.length === 0 ? (
              <div className="text-xs text-white/50 py-4 text-center">
                <CheckCircle size={22} weight="duotone" className="mx-auto mb-1 text-emerald-500/60" />
                Aucune alerte
              </div>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                {alerts.slice(0, 8).map((a, i) => (
                  <button
                    key={a.id}
                    onClick={() => a.chantier_id ? nav(`/chantiers/${a.chantier_id}`) : (a.kind === "chantier-urgent" && nav(`/chantiers/${a.id}`))}
                    className="w-full text-left rounded-lg bg-white/[0.03] hover:bg-white/[0.08] px-2 py-1.5 flex items-center gap-2 text-[11px] transition-colors"
                    data-testid={`dash-alert-${i}`}
                  >
                    {a.kind === "iv-sav" ? <Lightning size={11} weight="fill" className="text-amber-400 shrink-0" /> : <HardHat size={11} weight="duotone" className="text-red-400 shrink-0" />}
                    <span className="truncate flex-1">{a.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500/8 to-emerald-500/[0.02] border border-emerald-500/25 p-5">
              <button
                onClick={() => setMapOpen(true)}
                className="w-full text-left flex items-center gap-3 group"
                data-testid="dash-team-map-btn"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <MapTrifold size={20} weight="duotone" className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Temps réel</div>
                  <div className="font-display font-semibold text-sm">Carte équipe</div>
                  <div className="text-[10px] text-white/50">Voir où sont tes techs maintenant</div>
                </div>
                <ArrowRight size={14} className="text-emerald-400 group-hover:translate-x-0.5 transition-transform" weight="bold" />
              </button>
            </div>
          )}

          <button
            onClick={() => nav("/ia")}
            className="w-full rounded-2xl bg-gradient-to-br from-violet-500/10 to-violet-500/[0.02] border border-violet-500/25 p-5 text-left hover:border-violet-500/50 transition-colors group"
            data-testid="dash-ai-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-violet-500/20 flex items-center justify-center shrink-0">
                <Sparkle size={20} weight="duotone" className="text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-violet-400 font-semibold">IA</div>
                <div className="font-display font-semibold text-sm">Assistant BKflow</div>
                <div className="text-[10px] text-white/50">Débriefs, devis, planning</div>
              </div>
              <ArrowRight size={14} className="text-violet-400 group-hover:translate-x-0.5 transition-transform" weight="bold" />
            </div>
          </button>
        </section>
      </div>

      {/* Bottom row — Hours + Payroll (visible per-card RBAC handled inside components) */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-4">
        <TechHoursCard />
        <PayrollCard />
      </div>

      <TeamMapDialog open={mapOpen} onClose={() => setMapOpen(false)} />
    </div>
  );
}
